import { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { House } from '../../data/houses';
import { useApp } from '../../contexts/AppContext';

interface QuickPointsCardProps {
  house: House;
}

export default function QuickPointsCard({ house }: QuickPointsCardProps) {
  const { addPoints, getHouse } = useApp();
  const [customAmount, setCustomAmount] = useState('');
  const currentHouse = getHouse(house.id) || house;

  const quickAmounts = [1, 5, 10, 25, 50, 100];

  const handleQuickAdd = (amount: number) => {
    addPoints(house.id, amount, 'manual');
  };

  const handleQuickSubtract = (amount: number) => {
    addPoints(house.id, -amount, 'manual');
  };

  const handleCustomAdd = () => {
    const amount = parseInt(customAmount);
    if (!isNaN(amount) && amount > 0) {
      addPoints(house.id, amount, 'manual');
      setCustomAmount('');
    }
  };

  const handleCustomSubtract = () => {
    const amount = parseInt(customAmount);
    if (!isNaN(amount) && amount > 0) {
      addPoints(house.id, -amount, 'manual');
      setCustomAmount('');
    }
  };

  return (
    <div
      className="backdrop-blur-lg bg-white/10 rounded-2xl p-6 border-2 transition-all duration-300 hover:scale-[1.02]"
      style={{ borderColor: `${house.color}40` }}
    >
      <div className="flex items-center space-x-4 mb-6">
        <span className="text-4xl">{currentHouse.icon}</span>
        <div>
          <h3 className="text-xl font-bold text-white">{currentHouse.name}</h3>
          <p
            className="text-2xl font-bold"
            style={{ color: house.color }}
          >
            {currentHouse.points.toLocaleString()} pts
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {quickAmounts.map(amount => (
            <div key={amount} className="flex">
              <button
                onClick={() => handleQuickSubtract(amount)}
                className="flex-1 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-l-lg transition-colors text-sm font-medium"
              >
                -{amount}
              </button>
              <button
                onClick={() => handleQuickAdd(amount)}
                className="flex-1 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-r-lg transition-colors text-sm font-medium"
              >
                +{amount}
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center space-x-2 w-full">
          <button
            onClick={handleCustomSubtract}
            disabled={!customAmount || parseInt(customAmount) <= 0}
            className="flex-none p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Minus size={20} />
          </button>
          <input
            type="number"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            placeholder="Custom"
            className="flex-1 min-w-0 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-center placeholder-white/40 focus:outline-none focus:border-white/40"
          />
          <button
            onClick={handleCustomAdd}
            disabled={!customAmount || parseInt(customAmount) <= 0}
            className="flex-none p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
